/**
 * Reads a `.mcaddon` / `.mcpack` / raw pack `.zip` and classifies its entries.
 *
 * A `.mcaddon` is just a zip. It may contain one or more packs (behavior +
 * resource), each with its own `manifest.json`. We walk every entry and bucket
 * it by role so the analyzer can extract identifiers, lang keys and conflicts.
 */

import { unzipSync, strFromU8 } from "fflate";

export type PackType = "behavior" | "resource" | "skinpack" | "unknown";

export interface RawEntry {
  path: string;
  bytes: Uint8Array;
}

export interface ManifestInfo {
  path: string;
  dir: string; // directory the manifest lives in (the pack root)
  packType: PackType;
  headerName: string;
  headerUuid?: string;
  moduleUuids: string[];
}

export interface AddonContents {
  entries: RawEntry[];
  manifests: ManifestInfo[];
  /** JSON files under an `items/` directory. */
  itemFiles: { path: string; json: unknown }[];
  /** JSON files under a `recipes/` directory. */
  recipeFiles: { path: string; json: unknown }[];
  /** `.lang` files. */
  langFiles: { path: string; content: string }[];
  /** `item_texture.json` files. */
  itemTextureFiles: { path: string; json: unknown }[];
  /** Files we could not parse as JSON but expected to. */
  parseErrors: { path: string; error: string }[];
}

function dirOf(path: string): string {
  const idx = path.lastIndexOf("/");
  return idx === -1 ? "" : path.slice(0, idx);
}

function classifyManifest(json: any, path: string): ManifestInfo {
  const modules: any[] = Array.isArray(json?.modules) ? json.modules : [];
  const types = new Set(modules.map((m) => m?.type));
  let packType: PackType = "unknown";
  if (types.has("data") || types.has("script")) packType = "behavior";
  else if (types.has("resources")) packType = "resource";
  else if (types.has("skin_pack")) packType = "skinpack";

  return {
    path,
    dir: dirOf(path),
    packType,
    headerName: typeof json?.header?.name === "string" ? json.header.name : "(unnamed)",
    headerUuid: json?.header?.uuid,
    moduleUuids: modules.map((m) => m?.uuid).filter((u): u is string => typeof u === "string"),
  };
}

export function readAddon(bytes: Uint8Array): AddonContents {
  const unzipped = unzipSync(bytes);
  const entries: RawEntry[] = Object.entries(unzipped).map(([path, b]) => ({ path, bytes: b }));

  const contents: AddonContents = {
    entries,
    manifests: [],
    itemFiles: [],
    recipeFiles: [],
    langFiles: [],
    itemTextureFiles: [],
    parseErrors: [],
  };

  for (const entry of entries) {
    const path = entry.path;
    if (path.endsWith("/")) continue; // directory entry
    const lower = path.toLowerCase();
    const base = path.split("/").pop() ?? path;

    const tryJson = (): unknown | undefined => {
      try {
        return JSON.parse(stripBom(strFromU8(entry.bytes)));
      } catch (e) {
        contents.parseErrors.push({ path, error: (e as Error).message });
        return undefined;
      }
    };

    if (base === "manifest.json") {
      const json = tryJson();
      if (json) contents.manifests.push(classifyManifest(json, path));
    } else if (base === "item_texture.json") {
      const json = tryJson();
      if (json) contents.itemTextureFiles.push({ path, json });
    } else if (lower.endsWith(".lang")) {
      contents.langFiles.push({ path, content: stripBom(strFromU8(entry.bytes)) });
    } else if (lower.endsWith(".json") && /(^|\/)items\//.test(path)) {
      const json = tryJson();
      if (json) contents.itemFiles.push({ path, json });
    } else if (lower.endsWith(".json") && /(^|\/)recipes?\//.test(path)) {
      const json = tryJson();
      if (json) contents.recipeFiles.push({ path, json });
    }
  }

  return contents;
}

function stripBom(s: string): string {
  return s.charCodeAt(0) === 0xfeff ? s.slice(1) : s;
}
